import { memo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { EditorMode, NormalizedClinicalNote } from './types';

interface ClinicalNotesSidebarProps {
  notes: NormalizedClinicalNote[];
  activeNoteId: number | null;
  mode: EditorMode;
  onSelect: (noteId: number) => void;
  onCreate: () => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  formatDateTime: (value?: string | null) => string;
  previewText: (body: string) => string;
}

function SidebarComponent({
  notes,
  activeNoteId,
  mode,
  onSelect,
  onCreate,
  searchTerm,
  onSearchChange,
  formatDateTime,
  previewText,
}: ClinicalNotesSidebarProps) {
  return (
    <aside className="hidden h-full w-72 flex-shrink-0 flex-col gap-4 lg:flex">
      <Button size="sm" className="w-full" onClick={onCreate}>
        <Plus className="mr-2 h-4 w-4" /> Nuova nota
      </Button>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={searchTerm}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Cerca nelle note"
          className="pl-9"
        />
      </div>
      <nav className="flex-1 space-y-2 overflow-y-auto" aria-label="Note cliniche">
        {notes.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border/60 p-4 text-sm text-muted-foreground">
            Nessuna nota registrata.
          </p>
        ) : (
          notes.map((note) => {
            const isActive = note.id === activeNoteId && mode !== 'create';
            return (
              <button
                key={note.id}
                type="button"
                onClick={() => onSelect(note.id)}
                className={cn(
                  'w-full rounded-lg px-3 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  isActive
                    ? 'bg-primary/10 text-foreground'
                    : 'hover:bg-muted/30',
                )}
              >
                <span className="block text-xs text-muted-foreground">{formatDateTime(note.updated_at)}</span>
                <p className="mt-1 line-clamp-2 text-sm font-medium leading-snug text-foreground">
                  {previewText(note.body)}
                </p>
              </button>
            );
          })
        )}
      </nav>
    </aside>
  );
}

const ClinicalNotesSidebar = memo(SidebarComponent);

export default ClinicalNotesSidebar;
